import {
    domainColumns,
    formatOutOf,
    formatYesNo,
    getNumber,
    getScale,
    getScales,
    getString,
    mapScreeningLabel,
    type OutcomeTableConfig,
} from '../helpers';

const AQ_SUBSCALE_INTERPRETATION = 'Subescala (0-10): maior pontuação = mais traços';

export const aqConfig: OutcomeTableConfig = {
    title: 'Resultados AQ',
    columns: domainColumns,
    getRows: (outcomes) => {
        const scales = getScales(outcomes);
        const total = getScale(scales, 'total');

        return [
            {
                domain: 'Traços do espectro autista (total AQ)',
                result: formatOutOf(total.raw, 50),
                interpretation: getString(total.interpretation_label),
            },
            {
                domain: 'Rastreio clínico (ponto de corte >= 32)',
                result: formatYesNo(total.screening_positive),
                interpretation: mapScreeningLabel(total.screening_positive),
            },
            {
                domain: 'Habilidade social',
                result: formatOutOf(getScale(scales, 'social_skill').raw, 10),
                interpretation: AQ_SUBSCALE_INTERPRETATION,
            },
            {
                domain: 'Alternância de atenção',
                result: formatOutOf(getScale(scales, 'attention_switching').raw, 10),
                interpretation: AQ_SUBSCALE_INTERPRETATION,
            },
            {
                domain: 'Atenção a detalhes',
                result: formatOutOf(getScale(scales, 'attention_to_detail').raw, 10),
                interpretation: AQ_SUBSCALE_INTERPRETATION,
            },
            {
                domain: 'Comunicação',
                result: formatOutOf(getScale(scales, 'communication').raw, 10),
                interpretation: AQ_SUBSCALE_INTERPRETATION,
            },
            {
                domain: 'Imaginação',
                result: formatOutOf(getScale(scales, 'imagination').raw, 10),
                interpretation: AQ_SUBSCALE_INTERPRETATION,
            },
        ];
    },
    match: (outcomes) => {
        const scales = getScales(outcomes);
        return (
            'interpretation_label' in getScale(scales, 'total') &&
            'raw' in getScale(scales, 'social_skill') &&
            'raw' in getScale(scales, 'imagination')
        );
    },
};

const formatItemMean = (value: unknown) => {
    const mean = getNumber(value);
    return mean === null ? null : `Média por item: ${mean} (1-4)`;
};

export const uppspConfig: OutcomeTableConfig = {
    title: 'Resultados UPPS-P',
    columns: domainColumns,
    getRows: (outcomes) => {
        const scales = getScales(outcomes);
        const total = getScale(scales, 'total');
        const negativeUrgency = getScale(scales, 'negative_urgency');
        const premeditation = getScale(scales, 'lack_of_premeditation');
        const perseverance = getScale(scales, 'lack_of_perseverance');
        const sensationSeeking = getScale(scales, 'sensation_seeking');
        const positiveUrgency = getScale(scales, 'positive_urgency');

        return [
            {
                domain: 'Impulsividade (total UPPS-P)',
                result: formatOutOf(total.raw, 236),
                interpretation: getString(total.interpretation_label),
            },
            {
                domain: 'Urgência negativa',
                result: formatOutOf(negativeUrgency.raw, 48),
                interpretation: formatItemMean(negativeUrgency.mean),
            },
            {
                domain: 'Falta de premeditação',
                result: formatOutOf(premeditation.raw, 44),
                interpretation: formatItemMean(premeditation.mean),
            },
            {
                domain: 'Falta de perseverança',
                result: formatOutOf(perseverance.raw, 40),
                interpretation: formatItemMean(perseverance.mean),
            },
            {
                domain: 'Busca de sensações',
                result: formatOutOf(sensationSeeking.raw, 48),
                interpretation: formatItemMean(sensationSeeking.mean),
            },
            {
                domain: 'Urgência positiva',
                result: formatOutOf(positiveUrgency.raw, 56),
                interpretation: formatItemMean(positiveUrgency.mean),
            },
        ];
    },
    match: (outcomes) => {
        const scales = getScales(outcomes);
        return (
            'mean' in getScale(scales, 'negative_urgency') &&
            'mean' in getScale(scales, 'sensation_seeking')
        );
    },
};

const DERS_SUBSCALE_INTERPRETATION = 'Subescala: maior pontuação = mais dificuldade';

export const dersConfig: OutcomeTableConfig = {
    title: 'Resultados DERS',
    columns: domainColumns,
    getRows: (outcomes) => {
        const scales = getScales(outcomes);
        const total = getScale(scales, 'total');

        return [
            {
                domain: 'Dificuldades de regulação emocional (total DERS)',
                result: formatOutOf(total.raw, 180),
                interpretation: getString(total.interpretation_label),
            },
            {
                domain: 'Não aceitação de respostas emocionais',
                result: formatOutOf(getScale(scales, 'nonacceptance').raw, 30),
                interpretation: DERS_SUBSCALE_INTERPRETATION,
            },
            {
                domain: 'Dificuldades de engajamento em metas',
                result: formatOutOf(getScale(scales, 'goals').raw, 25),
                interpretation: DERS_SUBSCALE_INTERPRETATION,
            },
            {
                domain: 'Dificuldades de controle de impulsos',
                result: formatOutOf(getScale(scales, 'impulse').raw, 30),
                interpretation: DERS_SUBSCALE_INTERPRETATION,
            },
            {
                domain: 'Falta de consciência emocional',
                result: formatOutOf(getScale(scales, 'awareness').raw, 30),
                interpretation: DERS_SUBSCALE_INTERPRETATION,
            },
            {
                domain: 'Acesso limitado a estratégias',
                result: formatOutOf(getScale(scales, 'strategies').raw, 40),
                interpretation: DERS_SUBSCALE_INTERPRETATION,
            },
            {
                domain: 'Falta de clareza emocional',
                result: formatOutOf(getScale(scales, 'clarity').raw, 25),
                interpretation: DERS_SUBSCALE_INTERPRETATION,
            },
        ];
    },
    match: (outcomes) => {
        const scales = getScales(outcomes);
        return (
            'raw' in getScale(scales, 'nonacceptance') &&
            'raw' in getScale(scales, 'strategies') &&
            'raw' in getScale(scales, 'clarity')
        );
    },
};

// GADL is inverted: LOWER scores indicate clinical impairment, so every row
// relies on the api interpretation_label instead of mapScreeningLabel.
export const gadlConfig: OutcomeTableConfig = {
    title: 'Resultados GADL',
    columns: domainColumns,
    getRows: (outcomes) => {
        const scales = getScales(outcomes);
        const total = getScale(scales, 'total');
        const selfCare = getScale(scales, 'self_care');
        const domesticActivities = getScale(scales, 'domestic_activities');
        const complexActivities = getScale(scales, 'complex_activities');

        return [
            {
                domain: 'Funcionalidade global (total GADL)',
                result: formatOutOf(total.raw, 26),
                interpretation: getString(total.interpretation_label),
            },
            {
                domain: 'Autocuidado',
                result: formatOutOf(selfCare.raw, 10),
                interpretation: getString(selfCare.interpretation_label),
            },
            {
                domain: 'Atividades domésticas',
                result: formatOutOf(domesticActivities.raw, 8),
                interpretation: getString(domesticActivities.interpretation_label),
            },
            {
                domain: 'Atividades complexas',
                result: formatOutOf(complexActivities.raw, 8),
                interpretation: getString(complexActivities.interpretation_label),
            },
        ];
    },
    match: (outcomes) => {
        const scales = getScales(outcomes);
        return (
            'clinical_range' in getScale(scales, 'self_care') &&
            'clinical_range' in getScale(scales, 'domestic_activities')
        );
    },
};

export const qdepConfig: OutcomeTableConfig = {
    title: 'Resultados QEDP',
    columns: domainColumns,
    getRows: (outcomes) => {
        const scales = getScales(outcomes);
        const styleRow = (key: string, domain: string) => ({
            domain,
            result: formatOutOf(getScale(scales, key).raw, 5),
            interpretation: getString(getScale(scales, key).interpretation_label),
        });
        const dimensionRow = (key: string, domain: string, style: string) => ({
            domain,
            result: formatOutOf(getScale(scales, key).raw, 5),
            interpretation: `Dimensão do estilo ${style} (média 1-5)`,
        });

        return [
            styleRow('authoritative', 'Estilo democrático (autoritativo)'),
            styleRow('authoritarian', 'Estilo autoritário'),
            styleRow('permissive', 'Estilo permissivo'),
            dimensionRow('warmth_support', 'Afeto e apoio', 'democrático'),
            dimensionRow('regulation', 'Regulação', 'democrático'),
            dimensionRow('autonomy', 'Concessão de autonomia', 'democrático'),
            dimensionRow('physical_coercion', 'Coerção física', 'autoritário'),
            dimensionRow('verbal_hostility', 'Hostilidade verbal', 'autoritário'),
            dimensionRow('punitive', 'Punição', 'autoritário'),
            dimensionRow('indulgent', 'Indulgência', 'permissivo'),
        ];
    },
    match: (outcomes) => {
        const scales = getScales(outcomes);
        return 'raw' in getScale(scales, 'authoritative') && 'raw' in getScale(scales, 'indulgent');
    },
};
